if (!customElements.get('faq-simple')) {
  customElements.define('faq-simple', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.singleOpen = this.hasAttribute('single-open');
      this.items = new Map();
      const options = { signal: this.controller.signal };

      this.querySelectorAll('[data-faq-details]').forEach((details) => {
        const summary = details.querySelector('[data-faq-trigger]');
        const panel = details.querySelector('[data-faq-answer]');
        if (!summary || !panel) return;

        const item = { details, summary, panel, animation: null, targetOpen: details.open, groupName: details.getAttribute('name') };
        this.items.set(details, item);

        // Keep native disclosure available before initialization or without animation support.
        // In particular, do not reset a question the visitor has already opened.
        summary.addEventListener('click', (event) => {
          if (event.defaultPrevented) return;
          if (!this.singleOpen && (this.reducedMotion.matches || !panel.animate)) return;
          event.preventDefault();
          this.toggle(item, !(item.animation ? item.targetOpen : details.open));
        }, options);

        details.addEventListener('toggle', () => {
          if (details.open && (!item.animation || item.targetOpen)) this.closeOthers(item);
          // A native close must cancel an in-flight animation.
          if (!details.open && item.animation) this.finish(item, false);
        }, options);
      });

      // Native grouping handles the no-JS fallback. Once enhanced, close siblings
      // ourselves so their closing animation can finish before removing `open`.
      if (this.singleOpen) this.items.forEach((item) => item.details.removeAttribute('name'));

      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) this.finishAnimations();
      }, options);
      window.addEventListener('resize', () => this.finishAnimations(), options);

      ['shopify:block:select', 'shopify:block:deselect'].forEach((eventName) => {
        this.addEventListener(eventName, (event) => {
          const details = event.target.closest('[data-faq-item]')?.querySelector('[data-faq-details]');
          const item = this.items.get(details);
          if (item) this.toggle(item, eventName === 'shopify:block:select');
        }, options);
      });
    }

    closeOthers(currentItem) {
      if (!this.singleOpen) return;
      this.items.forEach((item) => {
        if (item !== currentItem && (item.animation ? item.targetOpen : item.details.open)) {
          this.toggle(item, false);
        }
      });
    }

    toggle(item, open) {
      const { details, summary, panel } = item;
      if (open) this.closeOthers(item);
      if (!open && panel.contains(document.activeElement)) summary.focus({ preventScroll: true });
      if (!item.animation && details.open === open) return;

      if (this.reducedMotion.matches || !panel.animate) {
        this.finish(item, open);
        return;
      }

      // Measure before cancelling so rapid clicks reverse from the visible height.
      const startHeight = details.open ? panel.getBoundingClientRect().height : 0;
      this.cancelAnimation(item);
      item.targetOpen = open;
      details.open = true;
      details.toggleAttribute('data-faq-closing', !open);
      panel.style.overflow = 'hidden';

      try {
        // Match the theme's collapsible-content timing without depending on its registration.
        item.animation = panel.animate(
          { height: [`${startHeight}px`, `${open ? panel.scrollHeight : 0}px`] },
          { duration: 500, easing: 'cubic-bezier(0.75, 0, 0.175, 1)' }
        );
        item.animation.onfinish = () => this.finish(item, open);
      } catch {
        // Animation failure must never block the native state change.
        this.finish(item, open);
      }
    }

    cancelAnimation(item) {
      if (!item.animation) return;
      item.animation.onfinish = null;
      item.animation.cancel();
      item.animation = null;
    }

    finish(item, open) {
      this.cancelAnimation(item);
      item.details.open = open;
      item.details.removeAttribute('data-faq-closing');
      item.panel.style.removeProperty('overflow');
      item.targetOpen = open;
    }

    finishAnimations() {
      this.items.forEach((item) => {
        if (item.animation) this.finish(item, item.targetOpen);
      });
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.finishAnimations();
      if (this.singleOpen) {
        this.items.forEach((item) => {
          if (item.groupName !== null) item.details.setAttribute('name', item.groupName);
        });
      }
      this.items.clear();
      this.controller = null;
    }
  });
}
